import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Chip, Stack, Divider,
  FormControl, InputLabel, Select, Grid, Card,
  CardContent, Avatar, Tooltip, IconButton
} from "@mui/material";
import {
  PlayArrow, Download, Refresh, AttachMoney,
  People, Business, AccountBalanceWallet, InfoOutlined
} from "@mui/icons-material";
import api from "../../api/axios";
import toast from "react-hot-toast";

/* ──────────────────────────────────────────────────────────
   HELPERS
────────────────────────────────────────────────────────── */

/**
 * Given year + month (1-based) + the company payroll policy,
 * compute the "reference date" to send to backend.
 *
 * The backend derives the actual cycle start/end from policy.
 * We just need to pass ANY date that falls within the desired cycle.
 *
 * Strategy:
 *   - For a cross-month cycle (e.g. 21→20):
 *       "June payroll" means cycle 21 May → 20 June → pass 20 June
 *   - For a same-month cycle (e.g. 1→31):
 *       Pass 15th of that month (always inside range)
 *
 * If we don't know the policy, just pass the 15th — backend handles it.
 */
const buildReferenceDate = (year, month, startDay = 21, endDay = 20) => {
  if (startDay <= endDay) {
    // Same-month cycle: pass mid-month
    return `${year}-${String(month).padStart(2, "0")}-15`;
  } else {
    // Cross-month cycle (e.g. 21→20): pass end day of target month
    return `${year}-${String(month).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`;
  }
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const statusColor = (s) => {
  if (!s) return "default";
  if (s === "GENERATED") return "success";
  if (s === "UPLOADED")  return "primary";
  return "default";
};

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

/* ──────────────────────────────────────────────────────────
   COMPONENT
────────────────────────────────────────────────────────── */
const HRPayrollAndDocs = () => {

  /* ─ Mode ─ */
  const [mode, setMode] = useState("ALL"); // ALL | BRANCH | EMPLOYEE

  /* ─ Month/Year picker ─ */
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-based

  /* ─ Filters ─ */
  const [branchId,    setBranchId]    = useState("");
  const [employeeId,  setEmployeeId]  = useState("");
  const [branches,    setBranches]    = useState([]);

  /* ─ Policy (for reference date computation) ─ */
  const [policy, setPolicy] = useState({ payrollStartDay: 21, payrollEndDay: 20 });

  /* ─ Data ─ */
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [error,    setError]    = useState("");

  /* ─ Summary stats ─ */
  const [stats, setStats] = useState(null);

  /* ─────────────────── INIT ─────────────────── */
  useEffect(() => {
    api.get("/branches").then(r => setBranches(r.data || [])).catch(() => {});
    api.get("/company-policy").then(r => {
      if (r.data) setPolicy(r.data);
    }).catch(() => {});
    loadHistory();
  }, []); // eslint-disable-line

  /* ─────────────────── REF DATE ─────────────────── */
  const refDate = buildReferenceDate(
    year, month,
    policy.payrollStartDay,
    policy.payrollEndDay
  );

  /* ─────────────────── LOAD MY HISTORY ─────────────────── */
  const loadHistory = async () => {
    try {
      const res = await api.get("/payroll/history/me");
      setHistory(res.data || []);
    } catch {
      // silently ignore — HR might not be an employee
    }
  };

  /* ─────────────────── GENERATE ─────────────────── */
  const runPayroll = async () => {
    setGenLoading(true);
    setError("");
    const tid = toast.loading("Generating payroll…");

    try {
      if (mode === "EMPLOYEE") {
        if (!employeeId) { setError("Enter employee ID"); setGenLoading(false); toast.dismiss(tid); return; }
        await api.post("/payroll/calculate", {
          employeeId: parseInt(employeeId),
          referenceDate: refDate
        });
        toast.success("Employee payroll generated", { id: tid });

      } else if (mode === "BRANCH") {
        if (!branchId) { setError("Select a branch"); setGenLoading(false); toast.dismiss(tid); return; }
        await api.post(`/payroll/calculate/branch/${branchId}?referenceDate=${refDate}`);
        toast.success("Branch payroll generated", { id: tid });

      } else {
        await api.post(`/payroll/calculate/all?referenceDate=${refDate}`);
        toast.success("All employees payroll generated", { id: tid });
      }

      loadHistory();
      await loadBranchReport();

    } catch (err) {
      const msg = err.response?.data?.message || "Payroll generation failed";
      setError(msg);
      toast.error(msg, { id: tid });
    } finally {
      setGenLoading(false);
    }
  };

  /* ─────────────────── BRANCH REPORT ─────────────────── */
  const loadBranchReport = async () => {
    if (!branchId && mode !== "ALL") return;
    setLoading(true);
    try {
      if (mode === "BRANCH" && branchId) {
        const res = await api.get(`/payroll/branch/${branchId}/history?referenceDate=${refDate}`);
        const data = res.data || [];
        setStats({
          total: data.length,
          totalNet: data.reduce((s, r) => s + (r.netSalary || 0), 0),
          totalGross: data.reduce((s, r) => s + (r.grossSalary || 0), 0),
          rows: data
        });
      } else if (mode === "ALL") {
        // Try aggregate report
        const cycleEnd   = refDate;
        const cycleStart = `${year}-${String(month === 1 ? 12 : month - 1).padStart(2,"0")}-${String(policy.payrollStartDay).padStart(2,"0")}`;
        const res = await api.get(`/payroll/reports/aggregate/all?cycleStartDate=${cycleStart}&cycleEndDate=${cycleEnd}`);
        setStats({ rows: res.data || [], aggregate: true });
      }
    } catch {
      // no report yet – that's fine
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────── DOWNLOAD PAYSLIP ─────────────────── */
  const downloadPayslip = async (id) => {
    try {
      const res = await api.get(`/payroll/payslip/download/${id}`, { responseType: "blob" });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `payslip_${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  /* ─────────────────── DOWNLOAD EXCEL ─────────────────── */
  const downloadExcel = async () => {
    try {
      let url;
      if (mode === "BRANCH" && branchId) {
        url = `/payroll/download/detailed/${branchId}?referenceDate=${refDate}`;
      } else {
        url = `/payroll/download/detailed-all?referenceDate=${refDate}`;
      }
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `payroll_${MONTHS[month-1]}_${year}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Excel download failed");
    }
  };

  /* ─────────────────── CYCLE LABEL ─────────────────── */
  const cycleLabel = (() => {
    const sd = policy.payrollStartDay;
    const ed = policy.payrollEndDay;
    if (sd > ed) {
      // cross-month: e.g. 21 May → 20 Jun for "June"
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear  = month === 1 ? year - 1 : year;
      return `${sd} ${MONTHS[prevMonth-1]} ${prevYear} → ${ed} ${MONTHS[month-1]} ${year}`;
    }
    return `${sd} → ${ed} ${MONTHS[month-1]} ${year}`;
  })();

  /* ──────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Page header ── */}
      <Paper elevation={0} sx={{
        p: 3, mb: 3, borderRadius: 3,
        background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)",
        color: "#fff", display: "flex", alignItems: "center", gap: 2
      }}>
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.15)", width: 48, height: 48 }}>
          <AttachMoney fontSize="large" />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={800}>Payroll & Payslips</Typography>
          <Typography variant="body2" sx={{ color: "#93c5fd" }}>
            Generate, review and download payroll for any month
          </Typography>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {/* ══════════════════════════════════════════════
          GENERATE PAYROLL CARD
      ══════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Generate Payroll
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">

          {/* Month */}
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                {MONTHS.map((m, i) => (
                  <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Year */}
          <Grid item xs={6} sm={2}>
            <TextField
              label="Year" size="small" type="number" fullWidth
              value={year} onChange={e => setYear(Number(e.target.value))}
              inputProps={{ min: 2023, max: 2100 }}
            />
          </Grid>

          {/* Mode */}
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Scope</InputLabel>
              <Select value={mode} label="Scope" onChange={e => setMode(e.target.value)}>
                <MenuItem value="ALL"><People sx={{ mr: 1, fontSize: 18 }} />All Employees</MenuItem>
                <MenuItem value="BRANCH"><Business sx={{ mr: 1, fontSize: 18 }} />Branch</MenuItem>
                <MenuItem value="EMPLOYEE"><AccountBalanceWallet sx={{ mr: 1, fontSize: 18 }} />Single Employee</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Branch dropdown */}
          {mode === "BRANCH" && (
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select value={branchId} label="Branch" onChange={e => setBranchId(e.target.value)}>
                  {branches.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Employee ID */}
          {mode === "EMPLOYEE" && (
            <Grid item xs={12} sm={3}>
              <TextField
                label="Employee ID" size="small" fullWidth
                value={employeeId}
                onChange={e => setEmployeeId(e.target.value)}
              />
            </Grid>
          )}

          {/* Generate button */}
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained" fullWidth
              startIcon={genLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
              onClick={runPayroll}
              disabled={genLoading}
              sx={{ py: 1 }}
            >
              {genLoading ? "Running…" : "Generate"}
            </Button>
          </Grid>
        </Grid>

        {/* Cycle label */}
        <Box mt={1.5} display="flex" alignItems="center" gap={1}>
          <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            Payroll cycle for <strong>{MONTHS[month-1]} {year}</strong>: &nbsp;{cycleLabel}
            &nbsp;(configured in Company Policy)
          </Typography>
        </Box>
      </Paper>

      {/* ══════════════════════════════════════════════
          ACTIONS BAR
      ══════════════════════════════════════════════ */}
      <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap">
        <Button
          variant="outlined" startIcon={<Download />}
          onClick={downloadExcel}
          size="small"
        >
          Download Excel
          {mode === "BRANCH" && branchId
            ? ` (${branches.find(b => b.id === branchId)?.name || "Branch"})`
            : " (All)"}
        </Button>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadBranchReport} size="small">
          Refresh Report
        </Button>
      </Stack>

      {/* ══════════════════════════════════════════════
          BRANCH / ALL REPORT TABLE
      ══════════════════════════════════════════════ */}
      {stats && !stats.aggregate && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700}>
              Payroll Report — {MONTHS[month-1]} {year}
            </Typography>
            {stats.total !== undefined && (
              <Stack direction="row" spacing={2}>
                <Chip label={`${stats.total} Employees`} color="primary" size="small" />
                <Chip label={`Gross ₹${fmt(stats.totalGross)}`} color="default" size="small" />
                <Chip label={`Net ₹${fmt(stats.totalNet)}`} color="success" size="small" />
              </Stack>
            )}
          </Stack>

          {loading ? <CircularProgress /> : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                  <TableCell><strong>Emp Code</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Designation</strong></TableCell>
                  <TableCell align="center"><strong>Expected Days</strong></TableCell>
                  <TableCell align="center"><strong>Worked Days</strong></TableCell>
                  <TableCell align="right"><strong>Gross</strong></TableCell>
                  <TableCell align="right"><strong>Deductions</strong></TableCell>
                  <TableCell align="right"><strong>Net Salary</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(stats.rows || []).map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{r.employeeCode || "—"}</TableCell>
                    <TableCell>{r.employeeName}</TableCell>
                    <TableCell>{r.designation || "—"}</TableCell>
                    <TableCell align="center">{r.expectedWorkingDays || "—"}</TableCell>
                    <TableCell align="center">{r.actualWorkedDays ?? "—"}</TableCell>
                    <TableCell align="right">₹{fmt(r.grossSalary)}</TableCell>
                    <TableCell align="right" sx={{ color: "error.main" }}>
                      ₹{fmt(r.totalDeductions)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "success.dark" }}>
                      ₹{fmt(r.netSalary)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {/* Aggregate (all-branch summary) */}
      {stats?.aggregate && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            All Branches Payroll Summary
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                <TableCell><strong>Branch</strong></TableCell>
                <TableCell align="center"><strong>Employees</strong></TableCell>
                <TableCell align="right"><strong>Total Net Payroll</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(stats.rows || []).map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>{r.branchName}</TableCell>
                  <TableCell align="center">{r.totalEmployees}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>₹{fmt(r.totalPayrollAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Divider sx={{ my: 3 }} />

      {/* ══════════════════════════════════════════════
          MY PAYSLIP HISTORY
      ══════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={700}>My Payslip History</Typography>
          <Button size="small" startIcon={<Refresh />} onClick={loadHistory}>Refresh</Button>
        </Stack>

        {history.length === 0 ? (
          <Typography color="text.secondary" variant="body2">No payroll records yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Pay Period</strong></TableCell>
                <TableCell align="right"><strong>Net Salary</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Payslip</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.id}</TableCell>
                  <TableCell>
                    {p.cycleStartDate} → {p.cycleEndDate}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    ₹{fmt(p.netSalary)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={p.status || "—"} color={statusColor(p.status)} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small" variant="outlined"
                      startIcon={<Download />}
                      onClick={() => downloadPayslip(p.id)}
                    >
                      PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default HRPayrollAndDocs;